import { useEffect, useRef } from "react";
// Figma specifies Iconly/Light/Arrow at 24x24 for this control (node 38304-44707). No
// asset in the repo carries that name; date-arrow-right.svg is the closest match in the
// same drawing style - stroked shaft plus head, stroke-width 1.46, round caps - and is
// already the right green (#178477).
//
// ONE import, mirrored with CSS for the left arrow. Its partner date-arrow-left.svg is
// stroke="black", so using the pair as shipped would put a black arrow opposite a green
// one. Mirroring guarantees the two match and removes the second file to keep in step.
import DateArrow from "../../../assets/images/icons/date-arrow-right.svg";

const MonthJumpSelector = ({
    isOpen,
    year,
    selectedMonthDate,
    minimumMonthDate,
    locale,
    onClose,
    onPreviousYear,
    onNextYear,
    onSelectMonth,
}) => {
    const selectorRef = useRef(null);
    const safeMinimumMonth = new Date(
        minimumMonthDate.getFullYear(),
        minimumMonthDate.getMonth(),
        1
    );

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }

        const handlePointerDown = (event) => {
            if (!selectorRef.current?.contains(event.target)) {
                onClose();
            }
        };
        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                onClose();
            }
        };

        document.addEventListener("mousedown", handlePointerDown);
        document.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("mousedown", handlePointerDown);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [isOpen, onClose]);

    if (!isOpen) {
        return null;
    }

    const selectedYear = selectedMonthDate.getFullYear();
    const selectedMonth = selectedMonthDate.getMonth();
    const isPreviousYearDisabled = year <= safeMinimumMonth.getFullYear();

    return (
        <div ref={selectorRef} className="month-jump-selector" role="dialog" aria-label="Select month">
            <div className="month-jump-selector__year">
                <button
                    type="button"
                    className="month-jump-selector__year-button"
                    onClick={onPreviousYear}
                    disabled={isPreviousYearDisabled}
                    aria-label="Previous year"
                >
                    <img
                        src={DateArrow}
                        alt=""
                        aria-hidden="true"
                        className="month-jump-selector__year-icon month-jump-selector__year-icon--left"
                    />
                </button>
                <strong>{year}</strong>
                <button
                    type="button"
                    className="month-jump-selector__year-button"
                    onClick={onNextYear}
                    aria-label="Next year"
                >
                    <img
                        src={DateArrow}
                        alt=""
                        aria-hidden="true"
                        className="month-jump-selector__year-icon"
                    />
                </button>
            </div>
            <div className="month-jump-selector__months">
                {Array.from({ length: 12 }, (_, monthIndex) => {
                    const targetDate = new Date(year, monthIndex, 1);
                    const isDisabled = targetDate < safeMinimumMonth;
                    const isSelected = year === selectedYear && monthIndex === selectedMonth;
                    const label = new Intl.DateTimeFormat(locale || "en", { month: "short" })
                        .format(targetDate)
                        .toUpperCase();

                    return (
                        <button
                            key={monthIndex}
                            type="button"
                            className={`month-jump-selector__month${isSelected ? " is-selected" : ""}`}
                            disabled={isDisabled}
                            onClick={() => onSelectMonth(targetDate)}
                        >
                            {label}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export default MonthJumpSelector;
